import { defineComponent as _defineComponent } from 'vue'
import { queries } from "@/apollo";
import {
  User,
  StatementType,
  RoleName,
  PositionType,
  TtUserReportRow,
} from "@/CodegenTypes";
import type { ModernTtUserReport } from "@/types/reportTypes";
import { first, sumBy, some } from "lodash";
import { formatDollar } from "@/helpers/formatDollar";

import { canSeeTtCompanyLevel } from "@/helpers/user/canSeeTtCompanyLevel";
import { FEATURE_TOGGLES } from "@/config";
import { computed, ref } from "vue";
import { useQuery } from "@vue/apollo-composable";
import { useRoute, useRouter } from "vue-router/composables";
import TtPositionStatementsModal from "@/components/modules/report/TtPositionStatementsModal.vue";
import TtTicketsModal from "@/components/modules/report/TtTicketsModal.vue";
import TtStrikeStatementsModal from "@/components/modules/report/TtStrikeStatementsModal.vue";
import { canSeeTtPopl } from "@/helpers/user/canSeeTtPopl";
import { useTranslation } from "@/store/translation";



export default /*#__PURE__*/_defineComponent({
  __name: 'IndividualFullReport',
  setup(__props) {

const route = useRoute();
const router = useRouter();
const { $t } = useTranslation();


const selectedStartReleaseDate = computed(() => {
  const query = route.query.startReleaseDate;
  return Array.isArray(query) ? first(query) ?? undefined : query;
});

const selectedEndReleaseDate = computed(() => {
  const query = route.query.endReleaseDate;
  return Array.isArray(query) ? first(query) ?? undefined : query;
});

const { result: meResult, loading: meLoading } = useQuery<{ me: User }>(
  queries.me,
  null,
  {
    fetchPolicy: "cache-first",
  }
);

const me = computed(() => meResult.value?.me);

const { result: fullReportResult, loading: fullReportLoading } = useQuery<{
  ttFullReport: ModernTtUserReport;
}>(queries.ttFullReport, {
  releaseDateStart: selectedStartReleaseDate,
  releaseDateEnd: selectedEndReleaseDate,
  userId: computed(() => route.query.userId),
});

const fullReport = computed(() => fullReportResult.value?.ttFullReport);

const { result: userResult, loading: userLoading } = useQuery<{ user: User }>(
  queries.user,
  {
    userId: computed(() => route.query.userId),
  }
);

const loading = computed(() => {
  return meLoading.value || fullReportLoading.value || userLoading.value;
});

const showStrikeModal = ref(false);
const showTicketsModal = ref(false);
const showPositionModal = ref(false);
const selectedUserId = ref<null | User["userId"]>(null);
const includeDownlines = ref(true);
const selectedStatementType = ref<StatementType>(StatementType.TicketIn);

const displayRole = computed(() => {
  const roleName = userResult.value?.user.roleName;
  if (roleName === RoleName.Manager) {
    return "Manager";
  } else {
    return "Upline";
  }
});

const seeCompanyLevel = computed(() => {
  return canSeeTtCompanyLevel(me.value);
});

const seePoPl = computed(() => {
  return canSeeTtPopl(me.value);
});

const isNotAdminViewingAdmin = computed(() => {
  return route.query?.userId === "admin" && me.value?.userId !== "admin";
});

const selectedUserStatement = computed(() => {
  if (!fullReport.value) {
    return null;
  }
  return [
    fullReport.value.consolidated,
    ...(fullReport.value.downlines ?? []),
  ].find((s) => s.userId === selectedUserId.value);
});


const totalPlaceoutTickets = computed(() => {
  const calculated = calcShareClaimAndTotal(fullReport.value?.consolidated);

  return {
    userId: fullReport.value?.consolidated.userId,
    userTicketRebate: fullReport.value?.consolidated.userTicketRebate ?? 0,
    amount: fullReport.value?.consolidated.amount ?? 0 + intakeTickets.value.amount,
    rebate: fullReport.value?.consolidated.rebate ?? 0 + intakeTickets.value.rebate,
    amountLessRebate: fullReport.value?.consolidated.amountLessRebate ?? 0 + intakeTickets.value.amountLessRebate,
    strikeAmount: fullReport.value?.consolidated.strikeAmount ?? 0 + intakeTickets.value.strikeAmount,
    fixedExpenses: fullReport.value?.consolidated.fixedExpenses ?? 0,
    net: fullReport.value?.consolidated.net ?? 0 + intakeTickets.value.net,
    companyStake: fullReport.value?.consolidated.companyStake ?? 0,
    userCompanyPl: fullReport.value?.consolidated.userCompanyPl ?? 0,
    shareClaim: calculated?.shareClaim ?? 0,
    total: calculated?.total ?? 0,
  };
});

const personalTickets = computed(() => {
  const result = {
    userId: fullReport.value?.consolidated.userId,
    userTicketRebate: fullReport.value?.consolidated.userTicketRebate ?? 0,
    amount: fullReport.value?.personalTickets.amount,
    rebate: fullReport.value?.personalTickets.rebate,
    amountLessRebate: fullReport.value?.personalTickets.amountLessRebate ?? 0,
    strikeAmount: fullReport.value?.personalTickets.strikeAmount ?? 0,
    // Custom calcs
    fixedExpenses: (fullReport.value?.personalTickets.fixedExpenses ?? 0),
    net: (fullReport.value?.personalTickets.net ?? 0),
    companyStake: fullReport.value?.personalTickets.companyStake ?? 0,
    userCompanyPl: fullReport.value?.personalTickets.userCompanyPl ?? 0,
    position: fullReport.value?.personalTickets.position ?? 0,
    netBal: fullReport.value?.personalTickets.netBal ?? 0,
    shareClaim: 0, // TODO:
    total: 0,
  };

  return result;
});





const downlinesWithTickets = computed(() => {
  return fullReport.value?.downlines
    ?.filter((dl) =>
      some(
        Object.values(dl).filter((val) => typeof val === "number" && val !== 0)
      )
    )
    .map((dl) => {
      const shareClaimPositionStatements = dl.positionStatements?.filter(
        (s) => s.positionType === PositionType.Outset
      );

      return {
        ...dl,
        shareClaim: shareClaimPositionStatements
          ? sumBy(shareClaimPositionStatements, (s) => s.uplinePosition)
          : 0,
        total: shareClaimPositionStatements
          ? sumBy(shareClaimPositionStatements, (s) => s.uplinePosition) +
            dl.netBal
          : dl.netBal,
      };
    });
});
const downlinesSummary = computed(() => {
  return {
    amount: sumBy(downlinesWithTickets.value, (dl) => dl.amount),
    rebate: sumBy(downlinesWithTickets.value, (dl) => dl.rebate),
    amountLessRebate: sumBy(
      downlinesWithTickets.value,
      (dl) => dl.amountLessRebate
    ),
    fixedExpenses: sumBy(downlinesWithTickets.value, (dl) => dl.fixedExpenses),
    strikeAmount: sumBy(downlinesWithTickets.value, (dl) => dl.strikeAmount),
    net: sumBy(downlinesWithTickets.value, (dl) => dl.net),
    companyStake: sumBy(downlinesWithTickets.value, (dl) => dl.companyStake),
    position: sumBy(downlinesWithTickets.value, (dl) => dl.position),
    netBal: sumBy(downlinesWithTickets.value, (dl) => dl.netBal),
    shareClaim: sumBy(downlinesWithTickets.value, (dl) => dl.shareClaim),
    total: sumBy(downlinesWithTickets.value, (dl) => dl.total),
  };
});

const personalPl = computed(() => {
  const downlineExpenses = sumBy(fullReport.value?.downlines, dl => dl.fixedExpenses);

  const strikeCommission = totalPlaceoutTickets.value.strikeAmount - (fullReport.value?.personalTickets.strikeAmount ?? 0) - sumBy(downlinesWithTickets.value, dl => dl.strikeAmount ?? 0) - intakeTickets.value.strikeAmount

  const result = {
    userId: fullReport.value?.consolidated.userId,
    // Custom calcs
    fixedExpenses: (fullReport.value?.consolidated.fixedExpenses ?? 0) - downlineExpenses - (fullReport.value?.personalTickets.fixedExpenses ?? 0),
    // rebate = ownRebates + intakeRebates - downlineRebates
    rebate: (fullReport.value?.consolidated.rebate ?? 0) - (fullReport.value?.personalTickets.rebate ?? 0) - (fullReport.value?.intakeTickets.rebate ?? 0) - sumBy(fullReport.value?.downlines, dl => dl.rebate),
    strikeCommission,
    position: (fullReport.value?.consolidated.position ?? 0) - (fullReport.value?.personalTickets.position ?? 0) - sumBy(downlinesWithTickets.value, (dl) => dl.position) ,
    groupPl: intakeTickets.value.net ?? 0,
    shareClaim: (totalPlaceoutTickets.value.shareClaim ?? 0) - (personalTickets.value.shareClaim ?? 0) - sumBy(downlinesWithTickets.value, (dl) => dl.shareClaim),
    total: 0,
  };

  result.total = result.groupPl + strikeCommission + result.fixedExpenses + result.rebate + result.position + result.shareClaim;

  return result;
});

const intakeTickets = computed(() => {
  if (!fullReport.value) return {
    userId: "",
    userTicketRebate: 0,
    amount: 0,
    rebate: 0,
    amountLessRebate: 0,
    strikeAmount: 0,
    net: 0,
  };
  return {
    userId: fullReport.value.consolidated.userId,
    userTicketRebate: fullReport.value.consolidated.userTicketRebate,
    amount: fullReport.value.intakeTickets.amount,
    rebate: fullReport.value.intakeTickets.rebate,
    amountLessRebate: fullReport.value.intakeTickets.amountLessRebate,
    strikeAmount: fullReport.value.intakeTickets.strikeAmount,
    net: fullReport.value.intakeTickets.net,
  };
});


const placeoutTickets = computed(() => {
  const result = {
    userId: fullReport.value?.consolidated.userId,
    userTicketRebate: fullReport.value?.consolidated.userTicketRebate,

    amount: fullReport.value?.consolidated.amount ?? 0 + intakeTickets.value.amount,
    rebate: fullReport.value?.consolidated.rebate ?? 0 + intakeTickets.value.rebate,
    amountLessRebate: fullReport.value?.consolidated.amountLessRebate ?? 0 + intakeTickets.value.amountLessRebate,
    strikeAmount: fullReport.value?.consolidated.strikeAmount ?? 0 + intakeTickets.value.strikeAmount,
    companyStake: fullReport.value?.consolidated.companyStake ?? 0,
    net: 0
  };

  result.net = result.amountLessRebate + result.strikeAmount;

  return result;
});

const netProfit = computed(() => {
  if (!fullReport.value) {
    return 0;
  }
  const netProfit = fullReport.value.personalTickets.netBal + personalTickets.value.fixedExpenses + personalPl.value.total
    
  if (Math.abs(netProfit) < 0.001) {
    return 0;
  }
  return netProfit;
});

function calcShareClaimAndTotal(user?: TtUserReportRow) {
  if (!user) {
    return undefined;
  }
  const shareClaimPositionStatements = user.positionStatements?.filter(
    (s) => s.positionType === PositionType.Outset
  );
  if (shareClaimPositionStatements?.length) {
    const shareClaim = sumBy(
      shareClaimPositionStatements,
      (s) => s.uplinePosition
    );
    return {
      ...user,
      shareClaim,
      total: shareClaim + user.netBal,
    };
  } else {
    return {
      ...user,
      shareClaim: 0,
      total: user.netBal,
    };
  }
}

function showPosition(userId?: User["userId"]) {
  if (userId) {
    selectedUserId.value = userId;
  }
  showPositionModal.value = true;
}


function showStrike(
  userId: User["userId"] | undefined,
  statementType: StatementType
) {
  if (userId) {
    selectedUserId.value = userId;
  }
  selectedStatementType.value = statementType;
  showStrikeModal.value = true;
}
function showTickets(
  userId: User["userId"] | undefined,
  includeDownlinesVar: boolean
) {
  if (userId) {
    selectedUserId.value = userId;
  }
  includeDownlines.value = includeDownlinesVar;
  showTicketsModal.value = true;
}

function seeAdmin() {
  router.push({
    name: "toto full report",
    query: { ...route.query, userId: "admin" },
  });
}

return { __sfc: true,route, router, $t, selectedStartReleaseDate, selectedEndReleaseDate, meResult, meLoading, me, fullReportResult, fullReportLoading, fullReport, userResult, userLoading, loading, showStrikeModal, showTicketsModal, showPositionModal, selectedUserId, includeDownlines, selectedStatementType, displayRole, seeCompanyLevel, seePoPl, isNotAdminViewingAdmin, selectedUserStatement, totalPlaceoutTickets, personalTickets, downlinesWithTickets, downlinesSummary, personalPl, intakeTickets, placeoutTickets, netProfit, calcShareClaimAndTotal, showPosition, showStrike, showTickets, seeAdmin, StatementType, formatDollar, FEATURE_TOGGLES, TtPositionStatementsModal, TtTicketsModal, TtStrikeStatementsModal }
}

})